import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';

import { select, Store } from '@ngrx/store';
import { Observable, of, Subscription } from 'rxjs';

import browser from 'browser-detect';

import { environment as env } from '../../environments/environment';

import { CurrentEmpresa } from '@rpe/registro-personas-empresas-lib';
import {
  AppState,
  authLogout,
  LocalStorageService,
  NotificationService,
  routeAnimations,
  selectEffectiveTheme,
  selectIsAuthenticated,
  selectSettingsLanguage,
  selectSettingsStickyHeader
} from '../core/core.module';
import {
  actionSettingsChangeAnimationsPageDisabled,
  actionSettingsChangeLanguage
} from '../core/settings/settings.actions';
import {
  selectAllPersonas,
  selectAuthPersona
} from '../features/persona/entity-personas/entity-personas.selectors';
import { State } from '../features/vista-usuario/vista-usuario.state';

import { User } from '../core/auth/auth.models';
import { selectAuthUser } from '../core/auth/auth.selectors';
import { selectAllEntidadesProductiva } from '../features/empresas/entity-entidad-productiva/entity-entidades-productiva.selectors';
import { selectAllCurrentEmpresa } from '../features/empresas/entity-mis-empresas/entity-current-empresa.selectors';
import { Persona } from '../features/persona/entity-personas/entity-personas.model';
import { Router } from '@angular/router';
import { selectAllPersonasFisica } from '../features/empresas/entity-personas-fisica/entity-personas-fisica.selectors';
import { map, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'rpe-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [routeAnimations]
})
export class AppComponent implements OnInit, OnDestroy {
  static idPersonaLogueada: number = undefined; // TODO modificar esto

  private subscriptions: Subscription[];

  isProd = env.production;
  envName = env.envName;
  version = env.versions.app;
  year = new Date().getFullYear();
  logo = 'assets/logo diapo alta.png';
  languages = ['en', 'de', 'sk', 'fr', 'es', 'pt-br', 'zh-cn', 'he'];
  navigation: [] = [
    /*
    { link: 'about', label: 'rpe.menu.about' },
    { link: 'feature-list', label: 'rpe.menu.features' },
    { link: 'examples', label: 'rpe.menu.examples' }*/
  ];
  navigationSideMenu: [] = [...this.navigation];

  persona$: Observable<Persona>;
  persona: Persona = null;
  cantidadEmpresas = 0;
  currentEmpresa: CurrentEmpresa = null;

  personas$: Observable<Persona[]> | undefined;
  isAuthenticated$: Observable<boolean> | undefined;
  stickyHeader$: Observable<boolean> | undefined;
  language$: Observable<string> | undefined;
  theme$: Observable<string> | undefined;

  constructor(
    private store: Store<AppState>,
    private storeVistaUsuario: Store<State>,
    private storageService: LocalStorageService,
    private notificationService: NotificationService,
    private router: Router
  ) {}

  public static getPersonaLogueada() {
    return this.idPersonaLogueada;
  }

  private static isIEorEdgeOrSafari() {
    return ['ie', 'edge', 'safari'].includes(browser().name || '');
  }

  ngOnInit(): void {
    this.storageService.testLocalStorage();
    if (AppComponent.isIEorEdgeOrSafari()) {
      this.store.dispatch(
        actionSettingsChangeAnimationsPageDisabled({
          pageAnimationsDisabled: true
        })
      );
    }

    const selectAllPersonasSubscription = this.storeVistaUsuario
      .pipe(select(selectAllPersonas))
      .subscribe((personas) => {
        if (personas.length === 0) {
          //this.store.dispatch(entityPersonasLoadRequest());
        } else {
          this.persona = personas[0];
          AppComponent.idPersonaLogueada = this.persona.id;
        }
      });

    const currentEmpresaSubscription = this.storeVistaUsuario
      .pipe(select(selectAllEntidadesProductiva))
      .pipe(
        switchMap((entidadesProductiva) => {
          this.cantidadEmpresas = entidadesProductiva.length;

          if (entidadesProductiva.length > 0) {
            if (
              entidadesProductiva[entidadesProductiva.length - 1]
                .persona_fisica_id
            ) {
              return this.storeVistaUsuario
                .pipe(select(selectAllPersonasFisica))
                .pipe(
                  map((personasFisica) => {
                    const currentPresona = personasFisica.find(
                      (persona) =>
                        persona.id ==
                        entidadesProductiva[entidadesProductiva.length - 1]
                          .persona_fisica_id
                    );

                    return {
                      id: entidadesProductiva[entidadesProductiva.length - 1]
                        .id,
                      persona_id: currentPresona.persona_id
                    };
                  }),
                  switchMap((currentEmpresa) => {
                    return this.storeVistaUsuario
                      .pipe(select(selectAllPersonas))
                      .pipe(
                        map((personas) => {
                          const currentPresona = personas.find(
                            (persona) => persona.id == currentEmpresa.persona_id
                          );

                          return {
                            id: currentEmpresa.id,
                            nombre: currentPresona
                              ? currentPresona.nombre_completo
                              : 'Persona Fisica'
                          };
                        }),
                        tap((currentEmpresa) => {
                          this.currentEmpresa = currentEmpresa;
                        })
                      );
                  })
                );
            }

            if (
              entidadesProductiva[entidadesProductiva.length - 1]
                .persona_juridica_id
            ) {
              return this.storeVistaUsuario
                .pipe(select(selectAllCurrentEmpresa))
                .pipe(
                  tap((currentEmpresa) => {
                    this.currentEmpresa =
                      currentEmpresa[currentEmpresa.length - 1];
                  })
                );
            }
          }
          return of(null);
        })
      )
      .subscribe();

    this.subscriptions = [
      selectAllPersonasSubscription,
      currentEmpresaSubscription
    ];

    // this.persona$ = this.store.pipe(select(selectPersonaId));
    this.isAuthenticated$ = this.store.pipe(select(selectIsAuthenticated));
    this.stickyHeader$ = this.store.pipe(select(selectSettingsStickyHeader));
    this.language$ = this.store.pipe(select(selectSettingsLanguage));
    this.theme$ = this.store.pipe(select(selectEffectiveTheme));
    this.persona$ = this.storeVistaUsuario.pipe(select(selectAuthPersona));

    this.store
      .select(selectAuthUser)
      .subscribe((user) => this.onLoggedUser(user));
  }

  onLogoutClick() {
    this.store.dispatch(authLogout());
  }

  onLanguageSelect(event: MatSelectChange) {
    this.store.dispatch(
      actionSettingsChangeLanguage({ language: event.value })
    );
  }

  onRedirectToProfile(): void {
    this.router.navigateByUrl('/perfil');
  }

  onLoggedUser(user: User) {
    if (!user) {
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe()
    );
  }

}
